import { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { usePopoverEditor } from '../../hooks/usePopoverEditor'

interface SelectOption {
  value: string
  label: string
}

interface InlineSelectEditorProps {
  /** Current selected value */
  value: string | null
  /** Field name for display purposes */
  fieldName: string
  /** Available options to select from */
  options: SelectOption[]
  /** Loading state for options */
  loading?: boolean
  /** Called when user saves a new value */
  onSave: (newValue: string | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
  /** Whether to allow clearing the selection */
  allowClear?: boolean
  /** Title shown at top of popover */
  title?: string
}

/**
 * Inline popover-style single-select editor matching the Options column style.
 * - Shows a list of options with radio-button style selection
 * - Auto-saves on click outside
 * - Cancels on Escape key
 */
export default function InlineSelectEditor({
  value,
  fieldName,
  options,
  loading = false,
  onSave,
  onCancel,
  allowClear = true,
  title
}: InlineSelectEditorProps) {
  const [selectedValue, setSelectedValue] = useState<string | null>(value)
  const [saving, setSaving] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)

  // Use a ref to always have access to the latest selectedValue in the click handler
  const selectedValueRef = useRef<string | null>(selectedValue)
  useEffect(() => {
    selectedValueRef.current = selectedValue
  }, [selectedValue])

  // Use a ref for saving state to avoid stale closures
  const savingRef = useRef(false)

  // The actual save function that uses refs to get latest values
  const doSave = useCallback(async () => {
    if (savingRef.current) return

    const latestSelected = selectedValueRef.current
    const hasChanged = latestSelected !== value

    if (!hasChanged) {
      onCancel()
      return
    }

    savingRef.current = true
    setSaving(true)
    try {
      await onSave(latestSelected)
    } catch (error) {
      savingRef.current = false
      setSaving(false)
    }
  }, [value, onSave, onCancel])

  const { position } = usePopoverEditor({
    onClickOutside: doSave,
    onCancel,
    popoverRef,
    anchorRef,
    popoverWidth: 200,
    popoverHeight: 300
  })

  const handleSelect = (optionValue: string | null) => {
    setSelectedValue(optionValue)
  }

  const popoverContent = (
    <div
      ref={popoverRef}
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[180px] max-h-[300px] overflow-y-auto"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' as const }}
      onClick={(e) => e.stopPropagation()}
    >
      {loading ? (
        <div className="text-sm text-gray-500 p-1">Loading...</div>
      ) : options.length === 0 ? (
        <div className="text-sm text-gray-500 p-1">No options available</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs font-medium text-gray-500">
              {title || `Select ${fieldName}`}
            </span>
            {allowClear && selectedValue && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="text-xs text-red-600 hover:text-red-800 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-1">
            {options.map((option) => {
              const isSelected = selectedValue === option.value
              return (
                <label
                  key={option.value}
                  className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                    isSelected ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <input
                    type="radio"
                    name={`select-${fieldName}`}
                    checked={isSelected}
                    onChange={() => handleSelect(option.value)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-900">{option.label}</span>
                </label>
              )
            })}
          </div>
          {saving && (
            <div className="text-xs text-gray-500 mt-2 px-1">Saving...</div>
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <span ref={anchorRef} />
      {ReactDOM.createPortal(popoverContent, document.body)}
    </>
  )
}

